import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import { ApiError } from './error';

export type SentryConnection = {
  provider: 'SENTRY';
  isConnected: boolean;
  connectedByMemberId: string | null;
  connectedByName: string | null;
  connectedAt: string | null;
  installationId: string | null;
  organizationId: string | null;
  organizationSlug: string | null;
  projectId: string | null;
  projectSlug: string | null;
};

export type SentryOrganization = { id: string; slug: string; name: string };
export type SentryProject = { id: string; slug: string; name: string };

export type SentryIssue = {
  id: string;
  shortId: string;
  title: string;
  culprit: string | null;
  permalink: string;
  status: string;
  level: string | null;
  count: number;
  firstSeen: string;
  lastSeen: string;
  project: SentryProject;
};

type Envelope<T> = { data: T };

const getSentryData = async <T>(token: string, path: string, fallback: string): Promise<T> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}${path}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });
  if (!response.ok) throw await parseApiError(response, `${fallback}: ${response.status}`);
  return ((await response.json()) as Envelope<T>).data;
};

export const buildSentryAuthConnectUrl = (token: string, redirectPath: string, projectId: string): string => {
  const url = new URL(`${getApiUrl()}/api/auth/sentry`);
  url.searchParams.set('redirect', redirectPath);
  url.searchParams.set('token', token);
  url.searchParams.set('projectId', projectId);
  return url.toString();
};

export const fetchSentryConnection = (token: string, projectId: string): Promise<SentryConnection> =>
  getSentryData(token, `/api/projects/${projectId}/sentry/connection`, 'Failed to fetch Sentry connection');

export const disconnectSentry = async (token: string, projectId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/sentry/connection`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!response.ok) throw await parseApiError(response, `Failed to disconnect Sentry: ${response.status}`);
};

export const fetchSentryOrganizations = (token: string, projectId: string): Promise<SentryOrganization[]> =>
  getSentryData(token, `/api/projects/${projectId}/sentry/organizations`, 'Failed to fetch Sentry organizations');

export const fetchSentryProjects = (
  token: string,
  projectId: string,
  organizationSlug: string,
): Promise<SentryProject[]> =>
  getSentryData(
    token,
    `/api/projects/${projectId}/sentry/organizations/${encodeURIComponent(organizationSlug)}/projects`,
    'Failed to fetch Sentry projects',
  );

export const saveSentrySelection = async (
  token: string,
  projectId: string,
  input: { organizationId: string; organizationSlug: string; sentryProjectId: string; projectSlug: string },
): Promise<SentryConnection> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/sentry/selection`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await parseApiError(response, `Failed to save Sentry selection: ${response.status}`);
  const payload = (await response.json()) as Envelope<{
    installationId: string;
    organizationId: string;
    organizationSlug: string;
    projectId: string;
    projectSlug: string;
  }>;
  return {
    provider: 'SENTRY',
    isConnected: true,
    connectedByMemberId: null,
    connectedByName: null,
    connectedAt: null,
    ...payload.data,
  };
};

export const isSentryReconnectRequired = (error: unknown): boolean =>
  error instanceof ApiError && error.statusCode === 409 && error.errorDetailCode === 'SENTRY_RECONNECT_REQUIRED';
